import _ from 'lodash'
import { gql, type ResultOf } from '~/tada/gql'
import { defineStore } from 'pinia'
import { festivalYear } from '~/stores/archive'

export type FiveFestFilm = ReturnType<typeof toFilm>

export const fiveFestPath = '/archive/fivefest'
export const fiveFestYearPath = (year: number) => `${fiveFestPath}/${year}`
export const fiveFestFilmPath = (year: number, id: number) => `${fiveFestPath}/${year}/${id}`

/** Films sampled for the Archive landing page's FiveFest shelf. */
const TEASER_SIZE = 12

/**
 * FilmFreeway entries were imported with "Film Freeway" as their production
 * company; that's where they came from, not who made them.
 */
const productionCompany = (company?: string | null) => {
  const c = (company ?? '').trim()
  return !c || /^film\s*freeway$/i.test(c) ? null : c
}

type FiveFestRow = ResultOf<typeof ArchiveFiveFestQuery>['festival_submissions'][number]

/** Shapes a submission like the Archive's team cards, so posters and stills render the same way. */
const toFilm = (s: FiveFestRow) => ({
  id: s.id,
  year: festivalYear({ id: s.film_festival_id }),
  film_name: s.film_title,
  name: productionCompany(s.production_company),
  genre: s.genre?.name ?? null,
  poster: s.poster,
  film: { mux_playback_id: s.mux_playback_id, mux_thumbnail_time: s.mux_thumbnail_time },
})

/**
 * FiveFest began in 2024, and only a few dozen films are submitted each year,
 * so every public FiveFest film loads in one light query; only a film's
 * tagline and synopsis wait until it's opened.
 */
export const useArchiveFiveFest = defineStore('archive-fivefest', () => {
  const router = useRouter()
  const route = computed(() => router.currentRoute.value)
  const inSection = computed(() => route.value.path.startsWith(fiveFestPath))

  const year = computed(() => inSection.value ? parseInt(route.value.params.year as string) || null : null)
  const filmId = computed(() => inSection.value ? parseInt(route.value.params.submissionId as string) || null : null)

  // The SATO48 year pages share the Archive middleware but show no FiveFest
  // films, so wait until the landing page or this section is opened.
  const wanted = ref(false)
  watchEffect(() => {
    if (route.value.path === '/archive' || inSection.value) wanted.value = true
  })
  const { result, loading } = useQuery(
    ArchiveFiveFestQuery,
    {},
    () => ({ clientId: 'anyone', enabled: wanted.value }),
  )
  const films = computed(() => (result.value?.festival_submissions ?? []).map(toFilm))
  const loaded = computed(() => !!result.value)

  /** Years with public FiveFest films, newest first. */
  const years = computed(() => _.chain(films.value).map('year').uniq().sortBy(y => -y).value())
  const byYear = computed(() => _.groupBy(films.value, 'year'))
  const yearFilms = computed(() => byYear.value[year.value ?? 0] ?? [])

  const film = computed(() => _.find(yearFilms.value, { id: filmId.value ?? undefined }))
  const { result: detailResult } = useQuery(
    ArchiveFiveFestFilmQuery,
    () => ({ id: filmId.value ?? 0 }),
    () => ({ clientId: 'anyone', enabled: filmId.value != null }),
  )
  const detail = computed(() => {
    const found = detailResult.value?.festival_submissions[0]
    return found?.id === filmId.value ? found : undefined
  })

  const neighbors = computed(() => {
    const list = yearFilms.value
    const i = _.findIndex(list, { id: filmId.value ?? undefined })
    if (i < 0) return { previous: undefined, next: undefined, upNext: [] }

    const n = list.length
    return {
      previous: list[(i - 1 + n) % n],
      next: list[(i + 1) % n],
      upNext: _.chain(list).slice(i + 1).concat(_.slice(list, 0, i)).take(6).value(),
    }
  })

  // Drawn once per visit, like the Archive's year covers.
  const sample = ref<number[]>([])
  watch(films, list => {
    if (!sample.value.length && list.length) sample.value = _.map(_.shuffle(list), 'id')
  }, { immediate: true })
  const shuffled = computed(() => _.sortBy(films.value, f => sample.value.indexOf(f.id)))
  const teaser = computed(() => _.take(shuffled.value.filter(f => f.poster.length), TEASER_SIZE))
  /** Up to three posters per year for the year tiles. */
  const covers = computed(() => _.mapValues(
    _.groupBy(shuffled.value.filter(f => f.poster.length), 'year'),
    list => _.take(list, 3).map(f => f.poster[0]!.file_name),
  ))

  const randomFilmPath = (inYear?: number | null) => {
    const pick = _.sample(inYear ? byYear.value[inYear] : films.value)
    return pick ? fiveFestFilmPath(pick.year, pick.id) : undefined
  }

  return {
    year,
    films,
    loaded,
    loading,
    years,
    byYear,
    yearFilms,
    film,
    detail,
    neighbors,
    teaser,
    covers,
    randomFilmPath,
  }
})

const ArchiveFiveFestQuery = gql(`query ArchiveFiveFestQuery {
  festival_submissions(order_by: [{film_festival_id: desc}, {film_title: asc}]) {
    id
    film_festival_id
    film_title
    production_company
    mux_playback_id
    mux_thumbnail_time
    genre {
      id
      name
    }
    poster: assets(where: {asset_type_id: {_eq: 21}}, order_by: {created_at: desc}, limit: 1) {
      id
      file_name
    }
  }
}`)

const ArchiveFiveFestFilmQuery = gql(`query ArchiveFiveFestFilmQuery($id: Int!) {
  festival_submissions(where: {id: {_eq: $id}}) {
    id
    tagline
    synopsis
  }
}`)
